import { gql } from '@apollo/client'

/**
 * Bulk disable / re-enable users at workspace or group scope.
 *
 * Contract: BULK_DISABLE_USERS_FE_HANDOFF.md. Supersedes
 * TOGGLE_MULTIPLE_USERS_DISABLED_IN_ORG, which is deprecated but still works.
 *
 * Scope is decided by companyId: present means group, absent means workspace.
 * There is no program scope — the API cannot express it.
 */

export type BulkUserOutcome = 'disabled'|'enabled'|'skipped'|'failed'

/** Why a user was skipped or failed. `message` is written to be shown as-is. */
export type BulkUserOutcomeReason =
  |'roleHolder'      // holds a protected role; cannot be disabled until it is removed
  |'self'
  |'alreadyInState'
  |'notInScope'
  |'notFound'        // includes pending invitees — an invitation is not a user
  |'transientError'

export type BulkUserOutcomeRow = {
  userId: string,
  userEmail?: string|null,
  outcome: BulkUserOutcome,
  reason?: BulkUserOutcomeReason|null,
  message?: string|null,
  at: number,
}

export type BulkUserJobCounts = {
  disabled: number,
  enabled: number,
  skipped: number,
  failed: number,
}

export type BulkUserJobStatus = 'queued'|'inprogress'|'done'

export type BulkUserJob = {
  id: string,
  scopeType: 'workspace'|'group',
  organizationId: string,
  companyId?: string|null,
  action: 'disable'|'enable',
  actorUserId: string,
  status: BulkUserJobStatus,
  total: number,
  /** Users with a recorded outcome — not messages dispatched. */
  processed: number,
  progress: number,
  counts: BulkUserJobCounts,
  actorEmail?: string|null,
  /** Skips and failures only, capped at 500. Null on list results. */
  outcomes?: BulkUserOutcomeRow[]|null,
  outcomesTruncated?: boolean|null,
  /**
   * Everyone the job acted on, successes included — the durable record the audit
   * criterion needs. Only on getBulkUserJob: it dominates document size, so the list
   * query and the progress subscription both project it away. Capped at 5000.
   */
  affectedUsers?: BulkUserOutcomeRow[]|null,
  affectedUsersTruncated?: boolean|null,
  createdAt: number,
  updatedAt: number,
  finishedAt?: number|null,
}

/** Mirrors the filter args the user lists are rendered with. */
export type BulkUserSelectionFilter = {
  searchText: string,
  filters?: {field: string, operator: string, value: any}[],
  excludeDisabled?: boolean,
  onlyDisabled?: boolean,
  /**
   * Rows the admin unticked while "select all matching" was active. The server
   * resolves the filter first and subtracts these, so ids the filter never matched
   * are simply ignored — the two lists do not have to agree. Max 5000.
   */
  excludedIds?: string[],
}

export type BulkToggleUsersDisabledInput = {
  organizationId: string,
  companyId?: string,
  disabled: boolean,
  /** Explicit selection, max 5000. Mutually exclusive with selectAll. */
  userIds?: string[],
  /** Server-resolved selection. Mutually exclusive with userIds. */
  selectAll?: BulkUserSelectionFilter,
}

const JOB_CORE_FIELDS = `
  id
  scopeType
  organizationId
  companyId
  action
  status
  total
  processed
  progress
  counts { disabled enabled skipped failed }
  actorEmail
  createdAt
  updatedAt
  finishedAt
`

const JOB_OUTCOME_FIELDS = `
  outcomes { userId userEmail outcome reason message at }
  outcomesTruncated
`

// Only getBulkUserJob populates these; the mutation and the list query project them
// away because a 5000-row map dominates the document. Requested separately so we do
// not ask for a field that is always null.
const JOB_AFFECTED_FIELDS = `
  affectedUsers { userId userEmail outcome reason message at }
  affectedUsersTruncated
`

/**
 * Starts the job. Returns as soon as the selection is resolved — users are still
 * being processed. `outcomes` here are pre-flight skips only (self, role holders),
 * which is what makes the confirmation summary honest.
 */
export const BULK_TOGGLE_USERS_DISABLED = gql`
  mutation BulkToggleUsersDisabled($input: BulkToggleUsersDisabledInput!) {
    res: bulkToggleUsersDisabled(input: $input) {
      ${JOB_CORE_FIELDS}
      ${JOB_OUTCOME_FIELDS}
    }
  }
`

/** One job including its skip/failure rows. Call when status reaches 'done'. */
export const GET_BULK_USER_JOB = gql`
  query GetBulkUserJob($jobId: String!) {
    res: getBulkUserJob(jobId: $jobId) {
      ${JOB_CORE_FIELDS}
      ${JOB_OUTCOME_FIELDS}
      ${JOB_AFFECTED_FIELDS}
    }
  }
`

/** Job history. Omits `outcomes` by design — too big for a list row. */
export const GET_BULK_USER_JOBS = gql`
  query GetBulkUserJobs(
    $organizationId: String!, $companyId: String, $skip: Int!, $limit: Int!
  ) {
    res: getBulkUserJobs(
      organizationId: $organizationId, companyId: $companyId, skip: $skip, limit: $limit
    ) {
      jobs { ${JOB_CORE_FIELDS} }
      count
    }
  }
`

/** Progress pushes: one immediately, then roughly every 5%, then on completion. */
export const LISTEN_BULK_USER_JOB_CHANGES = gql`
  subscription ListenBulkUserJobChanges($jobId: String!) {
    job: listenBulkUserJobChanges(jobId: $jobId) {
      id
      status
      total
      processed
      progress
      counts { disabled enabled skipped failed }
      finishedAt
    }
  }
`

/**
 * "A bulk user job is already running on this workspace (<jobId>). Wait for it to
 * finish." — the id is in the message, so we can offer to show that job rather than
 * surfacing a dead error.
 */
export const parseRunningJobId = (message?: string|null): string|null => {
  if (!message) return null
  const m = message.match(/already running on this workspace \(([^)]+)\)/)
  return m ? m[1] : null
}
